import { yupResolver } from '@hookform/resolvers/yup';
import { useKeycloak } from 'all-in-one-react-keycloak';
import { useClaimSubmission, ViewClaimResults, useOcrSubmit, useAnotherMemberValidate } from 'all-in-one-shared-api';
import { Alert, Form, Card } from 'antd';
import { PrimaryButton } from 'components/button';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Base64 from 'utils/Base64';
import getQueryValue from 'utils/getQueryValue';
// import { ClaimTabs } from '../../components/claimTab/ClaimTab';
// import ClaimAmountCard from './componentsForOcr/ClaimAmountCard';
import ClaimInfoCard from './componentsForOcr/ClaimInfoCard/ClaimInfoCard';
// import ClaimInfoCard from './components/ClaimInfoCard/ClaimInfoCard';
import DocumentsCard from './componentsForOcr/DocumentsCard';
import { ClaimDetailTitle } from './componentsForOcr/ClaimDetailTitle';
import { PartialClaimCard } from './componentsForOcr/PartialClaimCard';
import { ReviewPage } from './componentsForOcr/ReviewPage';
import { usePreviousClaims } from './hooks/usePreviousClaims';
import { useSelectedClaimTypeMappingForOcr } from './hooks/useSelectedClaimTypeMappingForOcr';
import { ClaimFormState, getClaimOcrSchema } from './schema';
import { NewClaimUtils } from './utils/newClaimUtils';
import { PreviousClaimModal } from './componentsForOcr/PreviousClaim/PreviousClaimModal';
import { PreviousClaimUtils } from './utils/previousClaimUtils';
import { breakpoint } from 'styled-components-breakpoint';
// import { useBreakpoint } from 'hooks';
import SubmitErrorPage from './SubmitErrorPage';
import { revalidateReactFormHook } from '../../i18n';
import { OCRResult } from 'all-in-one-shared-api/dist/apis/claims/interfaces';
import { DEFAULT_CLAIM_FORM_VALUE } from './constants';
const { Item: FormItem } = Form;
import { LoadingOutlined } from '@ant-design/icons';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 32px;
  column-gap: 18px;

  ${breakpoint('desktop')`
    display: grid;
    grid-template-columns: 1fr 200px;
  `}

  .ant-form-item-required::before {
    display: none !important;
  }
`;

const FromWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
`;

function NewClaimPage() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<number>(0);

  const methods = useForm<ClaimFormState>({
    resolver: yupResolver(getClaimOcrSchema(t, i18n.language)),
    defaultValues: DEFAULT_CLAIM_FORM_VALUE,
    context: { step }, // 传递当前步骤作为校验上下文
  });

  const [random, setRandom] = useState(Math.random());
  const [randomAnother, setRandomAnother] = useState(Math.random());
  const [ocrErrorTips, setOcrErrorTips] = useState<string | null | undefined>();
  useEffect(() => {
    revalidateReactFormHook(methods);
  }, [i18n.language, methods]);

  const location = useLocation();
  const navigate = useNavigate();

  const [copyFromPreviousClaimSuccess, setCopyFromPreviousClaimSuccess] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [ocrData, setOcrData] = useState<OCRResult | null>();
  const patientMemberType = methods.watch('patientMemberType');
  const claimTypeObject = methods.watch('claimTypeObject');
  const receipts = methods.watch('receipts');
  const [loading, setLoading] = useState(false);
  const [errorTips, setErrorTips] = useState('');
  const [showProgress, setShowProgress] = useState('');
  const [showProgressFlag, setShowProgressFlag] = useState(false);
  const [errors, setErrors] = useState<string | undefined>('');

  // map the claim code and required doc basic on claim type selected
  const { selectedClaimTypeObject } = useSelectedClaimTypeMappingForOcr({ methods });

  const { keycloak } = useKeycloak();
  const errorMapping: Record<string, string> = {
    'invalid.decimal.amount': 'newClaim.claimFailure.invalidAmount',
    'papi.claim.invalid.another.polnum.mid': 'newClaim.claimFailure.invalidPolicyNumber',
  };

  const { shouldFetch, mutate, error, isValidating } = useClaimSubmission(
    'claimSubmission',
    keycloak.token ?? '',
    NewClaimUtils.mapClaimSubmission(methods.getValues()),
    '',
    'WEB',
    ocrData?.ocrId ?? '',
    {
      shouldRetryOnError: false,
      revalidateOnMount: false,
      revalidateOnFocus: false,
      onSuccess: (data) => {
        setLoading(false);
        navigate('/claims/success', {
          state: {
            ...data,
          },
          replace: true,
        });
      },
      onError: () => {
        setLoading(false);
        setStep(2);
      },
    },
  );
  const {
    shouldFetch: ocrShouldfetch,
    data,
    error: ocrError,
    progress,
  } = useOcrSubmit(
    'getOcrdata' + random,
    keycloak.token ?? '',
    patientMemberType,
    receipts?.filter((item) => item.status !== 'removed')[0] ?? {},
  );

  const { shouldFetch: validAnotherShouldeFetch, error: validAnotherError } = useAnotherMemberValidate(
    'validAnother' + randomAnother,
    keycloak.token ?? '',
    methods.getValues('anotherPolicyNumber') ?? '',
    methods.getValues('anotherMemberId') ?? '',
    {
      onSuccess: (data) => {
        validAnotherShouldeFetch(false);
        setLoading(false);
        if (data.message === 'success') {
          setShowProgress(t('newClaim.start.uploading'));
          setShowProgressFlag(true);
          setErrorTips('');
          setErrors('');
          setRandom(Math.random());
          ocrShouldfetch(true);
        }
      },
    },
  );
  // handle fetch previous claim and previous claim modal state
  const { isHavePreviousClaim, previousClaims, previousClaimsCount, page, setPage } = usePreviousClaims({
    patientMemberType,
    selectedClaimTypeObject,
    step,
    pageIndex: 1,
    pageSize: 3,
  });

  // useEffect(() => {
  //   // Note(Anson Kan): back to page 1 when different claim types are selected
  //   setPage(1);
  // }, [claimTypeObject, setPage]);

  useEffect(() => {
    const encodedData = getQueryValue(location.search)('data');
    if (encodedData) {
      const dataEntries = JSON.parse(decodeURIComponent(Base64.decode(encodedData)));
      if (dataEntries) {
        methods.reset(PreviousClaimUtils.mapCopyClaimToForm(dataEntries));
        setCopyFromPreviousClaimSuccess(true);
      }
    }
  }, [location.search, methods]);

  const handleOnPreviousClaimClick = (prevClaim: ViewClaimResults) => {
    methods.reset({
      ...PreviousClaimUtils.mapPreviousClaimToForm(prevClaim, i18n.language),
      patientName: methods.getValues('patientName'),
      patientMemberType,
      claimTypeObject,
    });
    setShowModal(false);
    setCopyFromPreviousClaimSuccess(true);
  };
  useEffect(() => {
    if (progress && progress.percentage == 100) {
      setShowProgress(t('newClaim.start.processing'));
    }
    if (data) {
      setShowProgressFlag(false);
      setLoading(false);
      setOcrData(data);
      ocrShouldfetch(false);
      if (data.status === 'FAIL') setOcrErrorTips('newClaim.reviewInfo.ocrScanError');
      else if (data.isMedicalDocument == 'false') setOcrErrorTips('newClaim.reviewInfo.ocrNonMedical');
      else setOcrErrorTips(null);
      setShowProgress('');
      const obj = methods.getValues();
      methods.reset({
        ...obj,
        claimType:
          data.claimType.claimTypeCode === 'OP'
            ? data.receiptItems.length > 0
              ? data.claimType.claimTypeCode
              : ''
            : data.claimType.claimTypeCode,
        currency: data.currency,
        doctorId: data.doctorDetails.doctorCode,
        doctorName: i18n.language == 'en' ? data.doctorDetails.doctorName : data.doctorDetails.doctorChineseName,
        doctorNameQuery: i18n.language == 'en' ? data.doctorDetails.doctorName : data.doctorDetails.doctorChineseName,
        diagnosisCode: data.diagnosis.diagnosisCode,
        diagnosisName: {
          code: data.diagnosis.diagnosisCode,
          description: data.diagnosis.diagnosisDescription,
          chineseDescription: data.diagnosis.diagnosisChineseDescription,
        },
        treatmentDate: data.treatmentDate,
        claimTypeObject:
          data.claimType.claimTypeCode === 'OP'
            ? data.receiptItems.filter((item) => item.benefitCode).map((benefit) => `__${benefit.benefitCode}`)
            : data.claimType.claimTypeCode
            ? [data.claimType.claimTypeCode]
            : [],
        receiptAmounts:
          data.claimType.claimTypeCode === 'OP'
            ? data.receiptItems.map((item) => item.benefitAmount)
            : [data.totalAmount],
        otherDocuments: [],
        referralLetters: [],
      });
      setStep(1);
    }
    if (ocrError) {
      setShowProgressFlag(false);
      setShowProgress('');
      setLoading(false);
      ocrShouldfetch(false);
      setOcrErrorTips('newClaim.reviewInfo.ocrScanError');
      setStep(1);
    }
    // eslint-disable-next-line
  }, [data, ocrError, t, progress]);
  useEffect(() => {
    if (validAnotherError) {
      setErrors(validAnotherError?.code);
      validAnotherShouldeFetch(false);
      setLoading(false);
      setErrorTips(
        validAnotherError.code && errorMapping[validAnotherError?.code || '']
          ? t(errorMapping[validAnotherError.code])
          : validAnotherError?.message,
      );
    }
    // eslint-disable-next-line
  }, [validAnotherError, validAnotherShouldeFetch, t]);

  useEffect(() => {
    if (errors) {
      setErrorTips(t(errorMapping[errors]));
    }
    // eslint-disable-next-line
  }, [errors, t]);

  const handleErrors: SubmitErrorHandler<ClaimFormState> = (errors) => {
    console.log(errors);
  };
  const onSubmit = () => {
    setLoading(true);
    if (step == 0) {
      if (
        methods.getValues('isFirstClaim') === true ||
        (methods.getValues('isFirstClaim') === false &&
          methods.getValues('anotherMemberId') &&
          methods.getValues('anotherPolicyNumber'))
      ) {
        setRandomAnother(Math.random());
        validAnotherShouldeFetch(true);
      } else {
        setShowProgressFlag(true);
        setShowProgress(t('newClaim.start.uploading'));
        setRandom(Math.random());
        ocrShouldfetch(true);
      }
    }
    if (step === 1) {
      shouldFetch(true);
      mutate();
    }
  };
  // const claimTabs = [
  //   { translationKey: 'newClaim.claimInfo.title', key: 'claimInformation' },
  //   { translationKey: 'newClaim.partialClaim.title', key: 'partialClaim' },
  //   // { translationKey: 'newClaim.paymentInfo.receiptInfo', key: 'claimAmount' },
  //   { translationKey: 'newClaim.supplementaryDocuments.title', key: 'documents' },
  // ];

  if (step === 2) {
    const errorTranslationKey = errorMapping[error?.code || ''];
    const errorMessage = errorTranslationKey ? t(errorTranslationKey) : error?.message;
    return (
      <SubmitErrorPage
        handleOnStartOver={() => setStep(0)}
        errorMessage={errorMessage ?? t('newClaim.claimFailure.failedToSubmitDescription')}
      />
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, handleErrors)}>
        <Wrapper>
          <ClaimDetailTitle />

          {step === 0 && (
            <>
              <FromWrapper>
                {copyFromPreviousClaimSuccess && (
                  <Alert
                    showIcon
                    type="success"
                    message={t('newClaim.claimInfo.duplicatedClaimInfo.autoFilled.title')}
                    description={t('newClaim.claimInfo.duplicatedClaimInfo.autoFilled.description')}
                  />
                )}
                <Card>
                  <ClaimInfoCard
                    selectedClaimTypeObject={selectedClaimTypeObject}
                    hasPreviousClaim={isHavePreviousClaim}
                    handleOnPreviousClaimClick={() => setShowModal(true)}
                  />
                  <PartialClaimCard />
                  {/* <ClaimAmountCard selectedClaimTypeObject={selectedClaimTypeObject} /> */}
                  <DocumentsCard selectedClaimTypeObject={selectedClaimTypeObject} allowRemoveDocuments />
                </Card>
                <FormItem>
                  {errorTips && <Alert message={errorTips} type="error" closable style={{ marginBottom: '8px' }} />}
                  {showProgressFlag && (
                    <Alert
                      message={showProgress}
                      type="warning"
                      showIcon
                      icon={<LoadingOutlined />}
                      style={{ marginBottom: '8px', fontWeight: 'bold' }}
                    />
                  )}
                  <PrimaryButton loading={loading} htmlType="submit">
                    {t('common.next')}
                  </PrimaryButton>
                </FormItem>
              </FromWrapper>
              {/* {md && <ClaimTabs claimTabs={claimTabs} />} */}
            </>
          )}
          {step === 1 && (
            <ReviewPage
              selectedClaimTypeObject={selectedClaimTypeObject}
              handleOnBack={() => setStep(0)}
              isLoading={isValidating}
              ocrData={ocrData ?? undefined}
              ocrErrorTips={ocrErrorTips}
            />
          )}
        </Wrapper>
        <PreviousClaimModal
          isOpen={showModal}
          previousClaims={previousClaims}
          handleOnClose={() => setShowModal(false)}
          handleOnPreviousClaimClick={handleOnPreviousClaimClick}
          previousClaimsCount={previousClaimsCount}
          page={page}
          setPage={setPage}
        />
      </form>
    </FormProvider>
  );
}

export default NewClaimPage;
